import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useCurrentProduct } from '@atlassian/jira-invite-prompt-on-link-insert/src/utils/index.tsx';

import { requestAccess } from '../../services/access-request/index.tsx';
import { fireErrorAnalytics } from '../../services/analytics/index.tsx';
import { useFilteredUsersFromCollaborationGraph } from '../../services/get-collaborators/index.tsx';
import { useMsrRequestAccessErrorFlag } from '../request-access-error-flag/msr-request-access-error-flag.tsx';
import { useMsrRequestAccessSuccessFlag } from '../request-access-success-flag/msr-request-access-success-flag.tsx';
import {
	getAnalyticsAttributes,
	useMsrRequestAccessPromptFlag,
} from '../request-access-prompt-flag/msr-request-access-flag.tsx';
import { setHasSeenNudgeInLast7Days } from '../../services/caching/index.tsx';
import { RECOMMENDATION_PRODUCT_KEYS } from '../../services/recommendations/index.tsx';

const MAX_NUMBER_OF_JPD_COLLABORATORS = 10;
const CROSS_JOIN_NUDGES_JIRA_TO_JPD_INTEGRATION = 'crossJoinNudgesJiraToJpd';

export const useJpdMsrCrossJoinNudge = ({ sessionId }: { sessionId: string }) => {
	const cloudId = useCloudId();
	const userId = useAccountId() || '';
	const currentProduct = useCurrentProduct();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'jira-product-discovery',
		MAX_NUMBER_OF_JPD_COLLABORATORS,
	);
	const showMsrRequestAccessPromptFlag = useMsrRequestAccessPromptFlag();
	const showSuccessFlag = useMsrRequestAccessSuccessFlag();
	const showErrorFlag = useMsrRequestAccessErrorFlag();
	const { push } = useRouterActions();

	return useCallback(
		async ({ recommendations }: { recommendations: string[] }) => {
			try {
				const collaboratorsOnJpd = await getFilteredCollaborators();
				const onSubmit = async () => {
					try {
						const accessStatus = await requestAccess(
							'jira-product-discovery',
							currentProduct,
							cloudId,
							userId,
							'crossJoinNudgesJiraToJpd',
							createAnalyticsEvent,
						);
						if (
							accessStatus === 'ACCESS_GRANTED' ||
							accessStatus === 'ACCESS_EXISTS' ||
							accessStatus === 'APPROVED_REQUEST_EXISTS'
						) {
							push('/jira/projects');
						} else if (
							accessStatus === 'PENDING_REQUEST_CREATED' ||
							accessStatus === 'PENDING_REQUEST_EXISTS'
						) {
							showSuccessFlag();
						} else {
							showErrorFlag('jira-product-discovery');
						}
					} catch {
						showErrorFlag('jira-product-discovery');
					}
				};

				showMsrRequestAccessPromptFlag({
					users: collaboratorsOnJpd,
					onSubmit,
					targetProduct: 'jira-product-discovery',
					integration: CROSS_JOIN_NUDGES_JIRA_TO_JPD_INTEGRATION,
					eventAttributes: getAnalyticsAttributes({
						userId,
						users: collaboratorsOnJpd,
						integration: CROSS_JOIN_NUDGES_JIRA_TO_JPD_INTEGRATION,
						recommendationSessionId: sessionId,
						nudgeId: RECOMMENDATION_PRODUCT_KEYS.JPD,
						recommendations,
					}),
				});

				// this is used in the eligibility check for loom nudge
				await setHasSeenNudgeInLast7Days();

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics('showMsrNudge', error, {
					cloudId,
					targetProduct: 'jira-product-discovery',
					integration: CROSS_JOIN_NUDGES_JIRA_TO_JPD_INTEGRATION,
				});
			}
		},
		[
			cloudId,
			currentProduct,
			createAnalyticsEvent,
			getFilteredCollaborators,
			push,
			showErrorFlag,
			showMsrRequestAccessPromptFlag,
			showSuccessFlag,
			userId,
			sessionId,
		],
	);
};
